import { TEXTOS } from '../../lib/glossario'
import type { NumerosDoMes as Numeros } from '../../types/repositorios'

const INTEIRO = new Intl.NumberFormat('pt-BR')

interface Cartao {
  id: string
  rotulo: string
  valor: string
  ajuda: string
  /** Acento do cartão: o "sem solicitação" chama atenção; o resto é neutro. */
  acento?: string
}

/**
 * Os números do mês em cartões.
 *
 * Cada número vem do servidor com o mesmo recorte do resumo (até ontem): o que o texto diz e o
 * que o cartão mostra não têm como discordar. O cartão "sem solicitação citada" ganha acento
 * porque é o único que pede ação — e a ajuda diz, no mesmo cartão, que ele não é infração.
 *
 * @param props os totais do período
 * @returns a grade de cartões
 */
export function NumerosDoMes({ numeros }: { numeros: Numeros }) {
  const t = TEXTOS.repositorios.numeros
  const cartoes: Cartao[] = [
    { id: 'commits', rotulo: t.commits, valor: INTEIRO.format(numeros.commits), ajuda: t.commitsAjuda },
    { id: 'autores', rotulo: t.autores, valor: INTEIRO.format(numeros.autores), ajuda: t.autoresAjuda },
    { id: 'repositorios', rotulo: t.repositorios, valor: INTEIRO.format(numeros.repositorios), ajuda: t.repositoriosAjuda },
    {
      id: 'com-solicitacao',
      rotulo: t.comSolicitacao,
      valor: INTEIRO.format(numeros.comSolicitacaoCitada),
      ajuda: t.comSolicitacaoAjuda,
    },
    {
      id: 'sem-solicitacao',
      rotulo: t.semSolicitacao,
      valor: INTEIRO.format(numeros.semSolicitacaoCitada),
      ajuda: t.semSolicitacaoAjuda,
      acento: numeros.semSolicitacaoCitada > 0 ? 'bg-amber-400' : 'bg-slate-200',
    },
    { id: 'integracoes', rotulo: t.integracoes, valor: INTEIRO.format(numeros.integracoes), ajuda: t.integracoesAjuda },
    {
      id: 'linhas',
      rotulo: t.linhas,
      valor: `+${INTEIRO.format(numeros.adicoes)} / −${INTEIRO.format(numeros.remocoes)}`,
      ajuda: t.linhasAjuda(numeros.semDetalhe),
    },
  ]

  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {cartoes.map((cartao) => (
        <li key={cartao.id} className="cartao flex flex-col p-4">
          <span aria-hidden="true" className={`h-1.5 w-10 rounded-full ${cartao.acento ?? 'bg-slate-200'}`} />
          <span className="mt-3 text-sm font-medium text-slate-700">{cartao.rotulo}</span>
          <span className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{cartao.valor}</span>
          <span className="mt-1 text-xs leading-relaxed text-slate-500">{cartao.ajuda}</span>
        </li>
      ))}
    </ul>
  )
}
